import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  formatDate,
  formatQuantity,
  tickerOfSymbol,
  type OptimiseMethod,
  type OptimiseReport,
  type OptimiserRunEvaluation,
} from '@greencandle/api-client'
import { api } from '../../api'
import { annualFloorToDaily } from '../../quant'
import { Failed, pct } from '../primitives'
import { RiskFigure } from './risk'

/**
 * ML-2 / ML-3: the optimisers, their regime reading and the record of what they proposed.
 *
 * <p>Behind an explicit button, unlike every tile on the Overview tab. A proposal that appears on
 * page load reads as a fact about the portfolio rather than as an answer to a question nobody
 * asked, and D-09 is explicit that portfolio-theory output is framed as a scenario.
 */

export const METHODS: ReadonlyArray<readonly [OptimiseMethod, string, string]> = [
  ['HRP', 'Hierarchical Risk Parity (HRP)', 'Splits capital down a correlation tree. Inverts nothing, so it survives a near-singular covariance — and this book has a pair correlating at 0.98.'],
  ['ERC', 'Equal Risk Contribution (Risk Parity)', "Equalises each asset's share of portfolio variance. Needs no expected-return estimate."],
  ['CVAR', 'Minimum CVaR (tail risk)', 'Minimises the mean loss over the worst 5% of days rather than the variance of all of them — the difference between an asset that wobbles and one that occasionally falls off a cliff. Optionally under a floor on the return.'],
  ['REGIME', 'Markov Regime-Switching', "Reads the phase from the portfolio's own returns and scales ERC weights by the risk share the model implies."],
]

/**
 * SRV-2. The crises the optimiser can price a proposal against.
 *
 * <p>The ids mirror the sidecar's own (`analytics/app/stress.py`), and the labels here are only
 * what goes in the select: whatever the answer comes back naming is what gets rendered beside
 * the figures, so a name that drifts shows up as a name that disagrees rather than as a wrong
 * number.
 */
export const SCENARIOS: ReadonlyArray<readonly [string, string]> = [
  ['BLACK_MONDAY_1987', 'Black Monday, 1987'],
  ['GFC_2008', 'Global financial crisis, 2008'],
  ['COVID_2020', 'Covid crash, 2020'],
  ['RATES_2022', 'Rate shock, 2022'],
]



/**
 * FR-G3/G5 and UI-2, over the Python sidecar.
 *
 * <p>Three methods, offered as a menu and ranked nowhere: they answer different questions, and
 * which one is right is a mandate decision rather than an arithmetic one. Nothing computes
 * until the button is pressed - an optimiser that ran on page load would present a target
 * nobody asked for as though it were a fact.
 */
export function Optimiser({ accountId }: { accountId: string }) {
  const [method, setMethod] = useState<OptimiseMethod>('HRP')
  // Typed as a percent a year because that is how a return is thought about; sent as a mean
  // daily log return, because that is what the window is made of. Empty means no floor at all,
  // which is the honest default - nobody can estimate an expected return.
  const [floor, setFloor] = useState('')
  const [scenario, setScenario] = useState('')

  const returnTarget = annualFloorToDaily(floor)
  const stressScenario = scenario === '' ? undefined : scenario

  const run = useMutation({
    mutationFn: (chosen: OptimiseMethod) =>
      api.optimise(
        accountId,
        chosen,
        undefined,
        undefined,
        chosen === 'CVAR' ? returnTarget : undefined,
        stressScenario,
      ),
  })

  // All four, run together over the same holdings at the same moment - the only way the
  // comparison is honest, since each call reads live prices and a window ending "now".
  const compare = useMutation({
    mutationFn: () =>
      Promise.all(
        METHODS.map(([key]) =>
          api.optimise(
            accountId,
            key,
            undefined,
            undefined,
            key === 'CVAR' ? returnTarget : undefined,
            stressScenario,
          ),
        ),
      ),
  })

  const report: OptimiseReport | undefined = run.data
  const chosen = METHODS.find(([key]) => key === method)

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>Quant forecast and rebalancing (Python engine)</h3>
        <span className="panel-note">
          {report?.available
            ? `${report.observations} observations · ${report.from} — ${report.to}`
            : 'FR-G3 · ML-2 / ML-3'}
        </span>
      </div>

      <div className="controls-box">
        <div className="control-group">
          <label htmlFor="t-algo">Method</label>
          <select
            id="t-algo"
            value={method}
            onChange={(e) => setMethod(e.target.value as OptimiseMethod)}
          >
            {METHODS.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {method === 'CVAR' && (
          <div className="control-group">
            <label htmlFor="t-floor">Return floor, % a year</label>
            <input
              id="t-floor"
              type="number"
              step="0.5"
              placeholder="none"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
            />
          </div>
        )}
        <div className="control-group">
          <label htmlFor="t-crisis">Price the proposal under</label>
          <select
            id="t-crisis"
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
          >
            <option value="">no crisis</option>
            {SCENARIOS.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="control-group control-end">
          <button
            type="button"
            className="btn-outline"
            disabled={compare.isPending}
            onClick={() => compare.mutate()}
          >
            {compare.isPending ? 'Comparing…' : 'Compare all four'}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={run.isPending}
            onClick={() => run.mutate(method)}
          >
            {run.isPending ? 'Working…' : 'Recalculate'}
          </button>
        </div>
        <div className="commit-note">{chosen?.[2]}</div>
      </div>

      {compare.isError && <Failed error={compare.error} />}
      {compare.data && (
        <CompareTable
          reports={compare.data}
          selected={report?.available ? method : null}
          onSelect={(picked) => {
            setMethod(picked)
            run.mutate(picked)
          }}
        />
      )}

      {run.isError && <Failed error={run.error} />}

      {report && !report.available && (
        <div className="panel-gap">
          <span className="badge badge-custom">not available</span>
          <p>{report.reason}</p>
        </div>
      )}

      {report?.available && <OptimiseResult report={report} />}

      {!report && !run.isPending && !compare.data && (
        <p className="panel-foot">
          Pick a method and press Recalculate, or Compare all three to see how they differ.
          Nothing computes on its own: a target weight is a proposal rather than a fact, and it
          should not appear unasked.
        </p>
      )}
    </section>
  )
}

/**
 * The three methods side by side, answering the question the single view cannot: not "what
 * would HRP do" but "how do they differ". Comparable honestly only because all three ran just
 * now, over the same holdings - nothing here mixes windows or accounts.
 *
 * <p>Selecting a row loads that method into the full breakdown below, so this table is a way
 * in rather than a dead end: whichever method looks right on the summary is one click from its
 * complete weight-by-weight table.
 */
export function CompareTable({
  reports,
  selected,
  onSelect,
}: {
  reports: OptimiseReport[]
  selected: OptimiseMethod | null
  onSelect: (method: OptimiseMethod) => void
}) {
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>Method</th>
            <th className="right">Target vol</th>
            <th className="right">Target VaR</th>
            <th className="right">Tail loss</th>
            <th className="right">Target drawdown</th>
            <th>Biggest move</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => {
            const label = METHODS.find(([key]) => key === report.method)?.[1] ?? report.method
            const name = label.split(' (')[0]

            if (!report.available) {
              return (
                <tr key={report.method}>
                  <td>{name}</td>
                  <td colSpan={5} className="cell-sub">
                    {report.reason}
                  </td>
                  <td />
                </tr>
              )
            }

            // The one weight this method moves furthest from where the account stands today -
            // a single number that says more about a method's character than the average delta.
            const biggest = [...report.weights]
              .filter((w) => w.delta != null)
              .sort((a, b) => Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0))[0]

            return (
              <tr
                key={report.method}
                className={report.method === selected ? 'row-selected' : ''}
              >
                <td>
                  <strong>{name}</strong>
                  {!report.converged && (
                    <span className="cell-sub"> · did not converge</span>
                  )}
                </td>
                <td className="right num">{pct(report.proposed?.annualisedVolatility)}</td>
                <td className="right num down">{pct(report.proposed?.valueAtRisk)}</td>
                {/*
                  Expected shortfall for every row, including CVAR's - one estimator across the
                  four, which is the only way a column compares anything. CVAR's own optimum is
                  the same quantity measured a shade differently (it counts the boundary day
                  fractionally), and it is shown in the breakdown below rather than mixed in here.
                */}
                <td className="right num down">{pct(report.proposed?.expectedShortfall)}</td>
                <td className="right num down">{pct(report.proposed?.maxDrawdown)}</td>
                <td>
                  {biggest ? (
                    <>
                      {tickerOfSymbol(biggest.symbol)}{' '}
                      <span className={(biggest.delta ?? 0) > 0 ? 'up' : 'down'}>
                        {(biggest.delta ?? 0) > 0 ? '+' : ''}
                        {pct(biggest.delta)}
                      </span>
                    </>
                  ) : (
                    <span className="cell-sub">no material move</span>
                  )}
                </td>
                <td className="right">
                  <button
                    type="button"
                    className="linklike"
                    onClick={() => onSelect(report.method)}
                  >
                    View
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * INT-3. Whether the optimiser's past proposals actually worked out.
 *
 * <p>Every other panel on this page looks forward. This is the only one that ever looks back:
 * each run above is logged the moment it happens, and checked here at +30/+90/+180 days against
 * what the same fixed weights, held unrebalanced, actually returned. A run younger than 30 days
 * has nothing to show yet - that is not a missing feature, it is the true state of a forecast
 * nobody has had time to be right or wrong about.
 */
export function TrackRecord({ accountId }: { accountId: string }) {
  const history = useQuery({
    queryKey: ['optimiser-runs', accountId],
    queryFn: () => api.optimiserRuns(accountId),
  })

  if (history.isError) {
    return (
      <section className="panel">
        <div className="panel-header">
          <h3>Track record</h3>
        </div>
        <Failed error={history.error} />
      </section>
    )
  }
  if (!history.data) {
    return (
      <section className="panel">
        <div className="panel-header">
          <h3>Track record</h3>
        </div>
        <p className="muted">…</p>
      </section>
    )
  }

  const runs = history.data
  const evaluated = runs.filter((r) => r.evaluations.some((e) => e.available))

  return (
    <section className="panel">
      <div className="panel-header">
        <h3>Track record</h3>
        <span className="panel-note">
          {runs.length} run{runs.length === 1 ? '' : 's'} logged
        </span>
      </div>

      {runs.length === 0 ? (
        <p className="panel-foot">
          Nothing logged yet. Every time Recalculate or Compare all three runs above, the
          proposal is recorded here and checked at 30, 90 and 180 days against what actually
          happened - the only figure on this page that can ever say whether running the
          optimiser was worth it.
        </p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Run</th>
                <th>Method</th>
                <th className="right">Forecast vol</th>
                <th>+30d</th>
                <th>+90d</th>
                <th>+180d</th>
              </tr>
            </thead>
            <tbody>
              {runs.slice(0, 12).map((run) => {
                const label = METHODS.find(([key]) => key === run.method)?.[1].split(' (')[0]
                        ?? run.method
                return (
                  <tr key={run.id}>
                    <td className="cell-sub">{formatDate(run.runAt)}</td>
                    <td>{label}</td>
                    <td className="right num">{pct(run.proposedVolatility)}</td>
                    {[30, 90, 180].map((horizon) => (
                      <td key={horizon}>
                        <EvaluationCell
                          evaluation={run.evaluations.find((e) => e.horizonDays === horizon)}
                        />
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {runs.length > 0 && evaluated.length === 0 && (
        <p className="panel-foot">
          None of these are 30 days old yet, so nothing has a checked result. That is expected,
          not broken - the earliest a forecast can be judged is 30 days after it was made.
        </p>
      )}
    </section>
  )
}

/** One horizon's cell: not yet due, checked and available, or checked and refused with why. */
export function EvaluationCell({ evaluation }: { evaluation: OptimiserRunEvaluation | undefined }) {
  if (!evaluation) {
    return <span className="cell-sub">not due</span>
  }
  if (!evaluation.available) {
    return (
      <span className="cell-sub" title={evaluation.reason ?? undefined}>
        n/a
      </span>
    )
  }
  const tone = (evaluation.realisedReturn ?? 0) >= 0 ? 'up' : 'down'
  return (
    <span
      className={tone}
      title={
        evaluation.skippedSymbols.length > 0 ? (evaluation.reason ?? undefined) : undefined
      }
    >
      {(evaluation.realisedReturn ?? 0) >= 0 ? '+' : ''}
      {pct(evaluation.realisedReturn)}
      {evaluation.skippedSymbols.length > 0 ? ' *' : ''}
    </span>
  )
}

export function OptimiseResult({ report }: { report: OptimiseReport }) {
  const regime = report.regime

  return (
    <>
      {regime && (
        <div className={`term-verdict ${regime.state === 'STRESS' ? 'bad' : 'ok'}`}>
          <span className={`badge ${regime.state === 'STRESS' ? 'badge-custom' : 'badge-re'}`}>
            {regime.state === 'STRESS' ? 'Stress' : 'Calm'}
          </span>
          <strong>
            Stress probability {pct(regime.stressProbability)} · the model implies{' '}
            {pct(regime.defensiveShare)} in defensive assets
          </strong>
          <ul>
            <li>
              Calm-phase volatility {pct(regime.calmVolatility)} against{' '}
              {pct(regime.stressVolatility)} in stress; stress explains{' '}
              {pct(regime.stressShareOfWindow)} of the window.
            </li>
          </ul>
        </div>
      )}

      <div className="risk-figures">
        <RiskFigure
          label="Volatility now"
          value={report.current?.annualisedVolatility}
          hint="Annualised volatility of the portfolio as it stands"
        />
        <RiskFigure
          label="Target volatility"
          value={report.proposed?.annualisedVolatility}
          hint="The same measure for the proposed weights, over the same history"
        />
        <RiskFigure
          label="Target VaR"
          value={report.proposed?.valueAtRisk}
          tone="down"
          hint="Historical VaR of the proposed portfolio"
        />
        <RiskFigure
          label="Target drawdown"
          value={report.proposed?.maxDrawdown}
          tone="down"
          hint="Max drawdown of the proposed weights over this window"
        />
        {report.cvar != null && (
          <RiskFigure
            label="Tail minimised to"
            value={report.cvar}
            tone="down"
            hint="QNT-1: the mean loss over the worst days of the window, which is the quantity this method minimises"
          />
        )}
      </div>

      {report.stress && (
        <div className="term-verdict">
          <span className="badge badge-custom">{report.stress.name}</span>
          <strong>
            The book as it stands {pct(report.stress.currentDelta)} · as proposed{' '}
            {pct(report.stress.proposedDelta)}
          </strong>
          <ul>
            <li>
              One shock vector prices both, so the two differ because the weights do and for no
              other reason. Whether a rebalance helps in a crash is a different question from
              whether it lowers volatility.
            </li>
          </ul>
        </div>
      )}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Ticker</th>
              <th className="right">Current</th>
              <th className="right">Target</th>
              <th className="right">Delta</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {report.weights.map((row) => {
              const delta = row.delta ?? 0
              // A tenth of a percent is noise, not a trade worth placing.
              const material = Math.abs(delta) >= 0.001
              return (
                <tr key={row.symbol}>
                  <td>
                    <strong>{tickerOfSymbol(row.symbol)}</strong>
                  </td>
                  <td className="right num">{pct(row.current)}</td>
                  <td className="right num">
                    <strong>{pct(row.target)}</strong>
                  </td>
                  <td className={`right num ${delta > 0 ? 'up' : delta < 0 ? 'down' : ''}`}>
                    {delta > 0 ? '+' : ''}
                    {pct(delta)}
                  </td>
                  <td>
                    {!material ? (
                      <span className="cell-sub">no change</span>
                    ) : delta > 0 ? (
                      <span className="up">buy</span>
                    ) : (
                      <span className="down">trim</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {report.converged === false && (
        <p className="panel-foot warn">
          The optimiser did not fully converge — the weights are close, but should be read as
          approximate.
        </p>
      )}
      {report.netGreeks && report.netGreeks.optionContracts > 0 && (
        <p className="panel-foot warn">
          Not in this proposal: {report.netGreeks.optionContracts} option contracts carrying{' '}
          {formatQuantity(report.netGreeks.delta)} shares of delta and{' '}
          {formatQuantity(report.netGreeks.theta)} a day of theta. The optimiser prices only what
          has a price history, so these weights describe the rest of the book.
          {report.netGreeks.unpricedContracts > 0 &&
            ` ${report.netGreeks.unpricedContracts} more carry exposure the feed does not quote,
              so even that delta is understated.`}
        </p>
      )}
      {report.skipped.length > 0 && (
        <p className="panel-foot warn">
          Excluded: {report.skipped.join(', ')} — too little price history in the window to
              compare over.
        </p>
      )}
      {report.notes.map((note) => (
        <p className="panel-foot" key={note}>
          {note}
        </p>
      ))}
      <p className="panel-foot">
        Estimated over {report.observations} common daily observations from {report.from} to{' '}
        {report.to}, {report.assetsAnalysed} assets, confidence{' '}
        {pct(report.confidence)}. This is a scenario under stated assumptions, not a price forecast and
        not investment advice.
      </p>
    </>
  )
}
